import {
  DigestJobDetail,
  DigestJobResult,
  ReportChartDataRequestItem,
  ReportChartGroupCompareItemType,
  ReportChartJobIdResponse,
  ReportChartMeasureType,
  ReportFeedbackData,
} from '@vision/ui/interfaces';
import { toTableDispatchRequestDto } from '@vision/ui/services/table-dispatch.service.mapper';

type CachedChartResponseBase = {
  compare?: ReportChartGroupCompareItemType[];
  measure: ReportChartMeasureType;
};

export type CachedChartResponse =
  | (CachedChartResponseBase & {
      data: ReportFeedbackData;
      status: 'result';
    })
  | (CachedChartResponseBase & {
      data: ReportChartJobIdResponse;
      status: 'job';
    })
  | (CachedChartResponseBase & {
      status: 'missing';
    });

type CachedJobDetailWithLegacyParams = DigestJobDetail & {
  params?: Partial<ReportChartDataRequestItem['data']>;
  request?: Partial<ReportChartDataRequestItem['data']>;
};

const CACHED_ACTION_ALIASES: Record<string, string[]> = {
  funnel: ['campaign_funnel', 'funnel_campaign'],
  matrix: ['response_matrix'],
  metric: ['action_metric', 'action_metrics'],
};

const IGNORED_SIGNATURE_KEYS = new Set(['async', 'dashboard_widget_id', 'digest', 'key', 'current_user_id']);

type CachedRequestSignatureInput = Pick<ReportChartDataRequestItem, 'compare' | 'measure'> & {
  data: ReportChartDataRequestItem['data'] | Partial<ReportChartDataRequestItem['data']>;
};

function getCachedActionCandidates(url: string): string[] {
  const action = url.split('/').pop();

  if (!action) {
    return [];
  }

  return [action, ...(CACHED_ACTION_ALIASES[action] ?? [])];
}

function normalizeSignatureValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(normalizeSignatureValue);
  }

  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        if (IGNORED_SIGNATURE_KEYS.has(key)) {
          return acc;
        }

        const nextValue = (value as Record<string, unknown>)[key];
        if (nextValue === undefined || nextValue === null || nextValue === '') {
          return acc;
        }

        acc[key] = normalizeSignatureValue(nextValue);
        return acc;
      }, {});
  }

  return value;
}

export function getCachedRequestSignature({ compare, data, measure }: CachedRequestSignatureInput): string {
  let signatureData: unknown = data;

  if ('schemaSelections' in data) {
    const { campaignType, channelId, flowId, fromDate, nodeId, schemaSelections, toDate } = data;

    if (campaignType && channelId && flowId && fromDate && nodeId && schemaSelections && toDate) {
      // Cached signatures are wire-format contracts, even though chart requests are now camelCase.
      signatureData = toTableDispatchRequestDto({
        ...data,
        campaignType,
        channelId,
        flowId,
        fromDate,
        nodeId,
        schemaSelections,
        toDate,
      });
    }
  }

  return JSON.stringify(
    normalizeSignatureValue({
      compare,
      data: signatureData,
      measure,
    }),
  );
}

function cachedDetailHasResult(detail: DigestJobDetail): detail is DigestJobDetail & { result: ReportFeedbackData } {
  return Object.prototype.hasOwnProperty.call(detail, 'result');
}

function getDetailRequestParams(detail: DigestJobDetail): Partial<ReportChartDataRequestItem['data']> | undefined {
  const detailWithParams = detail as CachedJobDetailWithLegacyParams;

  return detailWithParams.request_params || detailWithParams.params || detailWithParams.request;
}

function detailMatchesRequestSignature(
  detail: DigestJobDetail,
  request: Pick<ReportChartDataRequestItem, 'compare' | 'data' | 'measure'>,
): boolean {
  if (detail.request_signature) {
    return detail.request_signature === getCachedRequestSignature(request);
  }

  const detailRequestParams = getDetailRequestParams(detail);
  if (!detailRequestParams) {
    return false;
  }

  return (
    getCachedRequestSignature({
      compare: request.compare,
      data: detailRequestParams,
      measure: request.measure,
    }) === getCachedRequestSignature(request)
  );
}

export function findCachedJobDetail({
  allowLegacyFallback = true,
  details,
  request,
  url,
}: {
  allowLegacyFallback?: boolean;
  details: DigestJobDetail[];
  request: Pick<ReportChartDataRequestItem, 'compare' | 'data' | 'measure'>;
  url: string;
}): DigestJobDetail | undefined {
  const actions = getCachedActionCandidates(url);
  const actionMatches = details.filter((detail) => actions.includes(detail.action));

  const signatureMatch = actionMatches.find((detail) => detailMatchesRequestSignature(detail, request));
  if (signatureMatch) {
    return signatureMatch;
  }

  if (!allowLegacyFallback && actionMatches.length > 1) {
    return undefined;
  }

  return actionMatches[0];
}

export function getCachedChartResponse({
  cachedResults,
  compare,
  data,
  measure,
  requireResult,
  url,
  widgetId,
}: {
  cachedResults?: DigestJobResult[] | null;
  compare?: ReportChartGroupCompareItemType[];
  data: ReportChartDataRequestItem['data'];
  measure: ReportChartMeasureType;
  requireResult?: boolean;
  url: string;
  widgetId?: string;
}): CachedChartResponse {
  const element = cachedResults?.find((element) => element.widget_id === widgetId);
  const detail = element
    ? findCachedJobDetail({
        allowLegacyFallback: !requireResult,
        details: element.details,
        request: { compare, data, measure },
        url,
      })
    : undefined;

  if (!detail) {
    return { compare, measure, status: 'missing' };
  }

  if (detail.status === 'completed' && cachedDetailHasResult(detail)) {
    return { compare, data: detail.result, measure, status: 'result' };
  }

  if (detail.status === 'completed' && !requireResult) {
    return { compare, data: { job_id: detail.job_id }, measure, status: 'job' };
  }

  return { compare, measure, status: 'missing' };
}
